/**
 * The journalutdrag — one patient's record on paper, every signed note included (DECISIONS.md #51).
 *
 * **What it is for.** A clinician who has to take a patient's documentation somewhere the box is
 * not — a transfer to another hospital, a ward without a working PC, the central EHR's own staff
 * after the outage — needs it in one document, readable without the system. That is this.
 *
 * **What it is not.** Not the version history: a corrected note appears once, as its current
 * version, marked *Rättad, version N*, and earlier versions stay in the record. The document says
 * so at the top, so nobody reads it as everything the record holds about every entry. And not a
 * hand-back to the central EHR in the structured sense (ROADMAP.md, phase 4) — it is paper, or the
 * PDF the browser makes of it.
 *
 * **Built from the signeringslista's parts and the overview's functions**, so the paper cannot say
 * one thing and the screen another: the same header, the same five safety slots from `safetyPanel`,
 * the medications through the shared dose formatter (`printableDose`), and the notes through the
 * same helpers the overview uses. The server sends every signed note for this document only; the
 * screen's ten-note cap is unchanged everywhere else.
 *
 * **The identification line repeats on every page** — the notes are a table whose `<thead>` the
 * browser repeats, the signeringslista's trick — so a page that continues cannot be read as
 * belonging to the patient named on the page before it. A note that fits on a page is kept on one
 * (`break-inside: avoid` on its `<tbody>`); a longer one breaks, because the alternative is a
 * blank half-page in front of every long note.
 */

import { formatClinicalDateOnly, formatClinicalDateTime } from '../format/clinicalDate'
import { deathBanner } from '../patient/deathDisplay'
import { guarded, patientName } from '../patient/patientDisplay'
import type { PatientNote, PatientOverview } from '../patient/patientOverviewApi'
import {
  medicationPreparation,
  medicationSchedule,
  noteAttribution,
  noteRecordedLater,
  noteTypeLabel,
  patientSex,
  practitionerName,
  safetyPanel,
} from '../patient/patientOverviewDisplay'
import { patientLocation } from '../patient/patientSearchDisplay'
import {
  identifierLines,
  printableDose,
  printedAtLine,
  wristbandIdentifier,
  type PrintTranslate,
} from './printDisplay'

/** Columns in the medication table. */
const MEDICATION_COLUMNS = 7

function NoteEntry({ note, t }: { readonly note: PatientNote; readonly t: PrintTranslate }) {
  const recordedLater = noteRecordedLater(note, t)

  const sections = [
    { key: 'subjective', label: t('patient:notes.subjective'), text: note.subjective },
    { key: 'objective', label: t('patient:notes.objective'), text: note.objective },
    { key: 'assessment', label: t('patient:notes.assessment'), text: note.assessment },
    { key: 'plan', label: t('patient:notes.plan'), text: note.plan },
  ].filter((section) => section.text !== null && section.text !== '')

  return (
    <div className="print-extract__note">
      <p className="print-extract__note-heading">
        <span className="print-extract__note-type">{noteTypeLabel(note.noteType, t)}</span>{' '}
        <span>{noteAttribution(note, t)}</span>
      </p>

      {/* The two things the overview says about a note that paper must say too: that it was
          written later than the time it describes (AGENTS.md §4), and that it was corrected. */}
      {recordedLater !== null && <p className="print-extract__note-meta">{recordedLater}</p>}
      {note.version > 1 && (
        <p className="print-extract__note-meta">
          {t('patient:notes.amended', { version: note.version })}
        </p>
      )}
      {/* DECISIONS.md #53 step 2 — on paper as on the screen. */}
      {note.forensicMarkerId !== null && (
        <p className="print-extract__note-meta print-extract__note-forensic">
          {t('print:extract.forensic')}
        </p>
      )}

      <div lang={note.language}>
        {sections.length > 0 && (
          <dl className="print-extract__soap">
            {sections.map((section) => (
              <div key={section.key}>
                <dt>{section.label}</dt>
                <dd>{section.text}</dd>
              </div>
            ))}
          </dl>
        )}
        {note.bodyText !== null && note.bodyText !== '' && (
          <p className="print-extract__text">{note.bodyText}</p>
        )}
      </div>
    </div>
  )
}

export function RecordExtract({
  overview,
  now,
  t,
}: {
  readonly overview: PatientOverview

  /** When this document was produced. See `AdministrationRecord` for why it is passed in. */
  readonly now: Date

  readonly t: PrintTranslate
}) {
  const location = overview.location
  const birthDate = overview.birthDate
  const name = patientName(overview, t)

  const leading = wristbandIdentifier(overview)
  const identification = t('print:mar.identification', {
    name,
    identifier: leading?.value ?? t('print:mar.noIdentifier'),
  })

  return (
    <article className="print-document print-extract">
      <header className="print-document__header">
        <p className="print-document__source">{t('print:document.source')}</p>
        <h1 className="print-document__title">{t('print:extract.heading')}</h1>

        <div className="print-document__patient">
          <p className="print-document__name">{name}</p>

          <dl className="print-document__facts">
            {identifierLines(overview, t).map((line) => (
              <div key={line.key}>
                <dt>{line.label}</dt>
                <dd>{line.value}</dd>
              </div>
            ))}

            <div>
              <dt>{t('patient:overview.birthDate')}</dt>
              <dd>
                {birthDate === null
                  ? t('common:value.notRecorded')
                  : guarded(() => formatClinicalDateOnly(birthDate), t)}
              </dd>
            </div>

            <div>
              <dt>{t('patient:overview.sex')}</dt>
              <dd>{patientSex(overview.sex, t)}</dd>
            </div>

            <div>
              <dt>{t('patient:overview.ward')}</dt>
              <dd>{location?.wardName ?? t('common:value.notRecorded')}</dd>
            </div>

            <div>
              <dt>{t('patient:overview.location')}</dt>
              <dd>
                {patientLocation(
                  { ward: location?.ward ?? null, bed: location?.bed ?? null },
                  t,
                )}
              </dd>
            </div>

            <div>
              <dt>{t('print:extract.admitted')}</dt>
              <dd>
                {location === null
                  ? t('common:value.notRecorded')
                  : guarded(() => formatClinicalDateTime(location.admittedAt), t)}
              </dd>
            </div>

            <div>
              <dt>{t('patient:overview.responsible')}</dt>
              <dd>
                {location === null
                  ? t('common:value.notRecorded')
                  : practitionerName(
                      location.responsibleGivenName,
                      location.responsibleFamilyName,
                      t,
                    )}
              </dd>
            </div>
          </dl>
        </div>

        {/* What the document holds and what it does not, before anybody reads it as more. */}
        <p className="print-extract__scope">{t('print:extract.scope')}</p>

        {/* A recorded death, in the header and before the safety block (DECISIONS.md #53): a
            journalutdrag of a deceased patient that said so only on page three would be read as a
            living patient's on page one. The same lines as the screen's banner. */}
        {overview.death !== null && (
          <section className="print-extract__death" aria-label={t('death:screen.sectionHeading')}>
            {deathBanner(overview.death, t).map((line, index) => (
              // By position: the circumstances are free text and could repeat a line.
              <p key={index} className={index === 0 ? 'print-extract__death-heading' : undefined}>
                {line}
              </p>
            ))}
          </section>
        )}
      </header>

      {/* First page, before everything else — AGENTS.md §5 on paper, as on the signeringslista.
          Monochrome-safe: level is carried by rule weight and words, not colour. */}
      <section className="print-document__safety" aria-label={t('print:mar.safetyHeading')}>
        <h2>{t('print:mar.safetyHeading')}</h2>

        <dl className="print-safety">
          {safetyPanel(overview, t).map((entry) => (
            <div key={entry.key} className={`print-safety__slot print-safety__slot--${entry.level}`}>
              <dt>{entry.label}</dt>
              <dd>
                <ul>
                  {entry.rows.map((row, index) => (
                    <li key={index}>
                      <span className="print-safety__value">{row.value}</span>
                      {row.detail !== null && (
                        <span className="print-safety__detail">{row.detail}</span>
                      )}
                      {row.attribution !== null && (
                        <span className="print-safety__attribution">{row.attribution}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="print-extract__medications">
        <h2>{t('print:extract.medicationsHeading')}</h2>

        {overview.medications.length === 0 ? (
          <p className="print-mar__none">{t('print:mar.noMedications')}</p>
        ) : (
          <table className="print-extract__table">
            <caption className="visually-hidden">{t('print:extract.medicationsCaption')}</caption>
            <thead>
              <tr className="print-mar__running">
                <th scope="colgroup" colSpan={MEDICATION_COLUMNS}>
                  {identification}
                </th>
              </tr>
              <tr>
                <th scope="col">{t('patient:medication.columnSubstance')}</th>
                <th scope="col">{t('patient:medication.columnPreparation')}</th>
                <th scope="col">{t('patient:medication.columnDose')}</th>
                <th scope="col">{t('patient:medication.columnRoute')}</th>
                <th scope="col">{t('patient:medication.columnSchedule')}</th>
                <th scope="col">{t('patient:medication.columnIndication')}</th>
                <th scope="col">{t('patient:medication.columnOrdered')}</th>
              </tr>
            </thead>
            <tbody>
              {overview.medications.map((medication) => {
                const dose = printableDose(medication)

                return (
                  <tr key={medication.medicationId}>
                    <td className="print-mar__substance">{medication.substance}</td>
                    <td>{medicationPreparation(medication, t)}</td>
                    <td
                      className={
                        dose === null
                          ? 'print-mar__dose print-mar__dose--unprintable'
                          : 'print-mar__dose'
                      }
                    >
                      {dose ?? t('print:mar.doseUnprintable')}
                    </td>
                    <td>{medication.route}</td>
                    <td>{medicationSchedule(medication, t)}</td>
                    <td>{medication.indication}</td>
                    <td>
                      {guarded(() => formatClinicalDateTime(medication.startAt), t)}
                      <br />
                      {practitionerName(
                        medication.prescriberGivenName,
                        medication.prescriberFamilyName,
                        t,
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="print-extract__notes">
        <h2>
          {t('print:extract.notesHeading')}
          {/* The count only when there is something to count: "(0 …)" above "Inga signerade
              anteckningar" says the same thing twice (seen in the first print, 2026-10-09). */}
          {overview.notes.length > 0 && (
            <>
              {' '}
              <span className="print-extract__count">
                ({t('print:extract.notesCount', { count: overview.notes.length })})
              </span>
            </>
          )}
        </h2>

        {overview.notes.length === 0 ? (
          <p className="print-mar__none">{t('print:extract.noNotes')}</p>
        ) : (
          <table className="print-extract__notes-table">
            <caption className="visually-hidden">{t('print:extract.notesCaption')}</caption>
            <thead>
              <tr className="print-mar__running">
                <th scope="col">{identification}</th>
              </tr>
            </thead>
            {overview.notes.map((note) => (
              // One tbody per note, kept on one page when it fits — this file's header says why.
              <tbody key={`${note.noteId}-${note.version}`} className="print-extract__note-group">
                <tr>
                  <td>
                    <NoteEntry note={note} t={t} />
                  </td>
                </tr>
              </tbody>
            ))}
          </table>
        )}
      </section>

      <footer className="print-document__footer">
        <p>{printedAtLine(now, t)}</p>
        <p>{t('print:document.verify')}</p>
      </footer>
    </article>
  )
}
